import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProductCard from "@/components/ProductCard";
import TrustTierBadge from "@/components/TrustTierBadge";
import PostCard from "@/components/PostCard";
import { BroadcastIcon, CalendarCheckIcon, PackageIcon, UsersThreeIcon, ChatCenteredDotsIcon } from "@phosphor-icons/react";

const TABS = [
  { id: "all", label: "All", testid: "feed-tab-all" },
  { id: "live", label: "Live now", testid: "feed-tab-live" },
  { id: "scheduled", label: "Upcoming", testid: "feed-tab-scheduled" },
  { id: "listings", label: "Drops", testid: "feed-tab-listings" },
  { id: "posts", label: "Posts", testid: "feed-tab-posts" },
  { id: "people", label: "People", testid: "feed-tab-people" },
];

export default function FollowingFeed() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  // Default to whatever ?tab= the URL specifies — lets us deep-link straight
  // to "People" from the navbar menu. Falls back to the activity feed view.
  const initialTab = TABS.some((t) => t.id === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "all";
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(null); // [{user_id, username, ...}]
  const [busyUserId, setBusyUserId] = useState(null);

  // Keep tab and ?tab= in sync — bookmarks and back-button work as expected.
  useEffect(() => {
    const current = searchParams.get("tab");
    if (current === tab) return;
    const next = new URLSearchParams(searchParams);
    if (tab === "all") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/me/feed");
        setData(r.data);
      } catch {}
      setLoading(false);
    })();
  }, [user]);

  // Fetch the explicit "people I follow" list lazily — only when the
  // visitor opens the People tab so we don't hit /me/following on every
  // pageload of the feed.
  useEffect(() => {
    if (!user || tab !== "people" || following !== null) return;
    (async () => {
      try {
        const r = await api.get("/me/following");
        setFollowing(r.data?.items || r.data || []);
      } catch {
        setFollowing([]);
      }
    })();
  }, [tab, user, following]);

  const unfollow = async (uid) => {
    setBusyUserId(uid);
    try {
      await api.delete(`/sellers/${uid}/follow`);
      setFollowing((cur) => (cur || []).filter((u) => (u.user_id || u.seller_id) !== uid));
    } catch {
      // swallow — UI revert handled by next refresh
    } finally {
      setBusyUserId(null);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto p-10 text-center">
          <p className="font-display font-bold text-2xl">Sign in to see your feed.</p>
          <Link to="/login" className="text-[#1D78AB] font-semibold mt-2 inline-block">
            Log in →
          </Link>
        </div>
      </div>
    );
  }

  const live = data?.live || [];
  const scheduled = data?.scheduled || [];
  const listings = data?.listings || [];
  const posts = data?.posts || [];
  const showLive = tab === "all" || tab === "live";
  const showSched = tab === "all" || tab === "scheduled";
  const showListings = tab === "all" || tab === "listings";
  const showPosts = tab === "all" || tab === "posts";

  const empty = !loading && !live.length && !scheduled.length && !listings.length && !posts.length;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="following-feed-page">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="overline text-zinc-500">YOUR FEED</p>
            <h1 className="font-display font-black text-4xl tracking-tighter mt-2">
              Activity from people you follow
            </h1>
            <p className="text-sm text-zinc-600 mt-1 flex items-center gap-1.5">
              <UsersThreeIcon size={14} /> Following {data?.following_count ?? 0}{" "}
              {data?.following_count === 1 ? "person" : "people"}
            </p>
          </div>
          <Link to="/live" className="text-sm font-semibold text-[#1D78AB]" data-testid="feed-discover-link">
            Discover new sellers →
          </Link>
        </div>

        <div className="mt-6 flex gap-1 border-b border-zinc-200">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
              className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition ${
                tab === t.id
                  ? "border-[#1D78AB] text-[#1D78AB]"
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div className="p-10 text-zinc-500">Loading your feed…</div>}

        {empty && tab !== "people" && (
          <div className="mt-10 border border-zinc-200 p-12 text-center bg-zinc-50">
            <p className="font-display font-bold text-xl">Your feed is empty.</p>
            <p className="text-sm text-zinc-600 mt-1">
              Follow sellers to see their drops, live shows and scheduled streams here.
            </p>
            <Link to="/live" className="mt-4 inline-block bg-[#1D78AB] text-white px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="feed-empty-discover">
              Browse live sellers
            </Link>
          </div>
        )}

        {showLive && live.length > 0 && (
          <section className="mt-8" data-testid="feed-live">
            <h2 className="overline text-zinc-500 flex items-center gap-2">
              <BroadcastIcon size={14} className="text-rose-500" />
              LIVE NOW
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
              {live.map((s) => (
                <Link
                  key={s.id}
                  to={`/stream/${s.id}`}
                  className="card-modern p-4 bg-white hover:shadow-lg transition"
                  data-testid={`feed-live-${s.id}`}
                >
                  <div className="flex items-center gap-2 text-xs text-rose-600 font-bold tracking-wider">
                    <span className="pill pill-live">LIVE</span>
                    <span>{s.viewer_count || 0} watching</span>
                  </div>
                  <p className="mt-2 font-display font-black text-lg line-clamp-2">{s.title}</p>
                  <p className="text-sm text-zinc-600 mt-1">by {s.seller_name}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {showSched && scheduled.length > 0 && (
          <section className="mt-8" data-testid="feed-scheduled">
            <h2 className="overline text-zinc-500 flex items-center gap-2">
              <CalendarCheckIcon size={14} className="text-amber-500" />
              UPCOMING
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
              {scheduled.map((s) => (
                <div
                  key={s.id}
                  className="card-modern p-4 bg-white"
                  data-testid={`feed-sched-${s.id}`}
                >
                  <span className="pill pill-scheduled">SCHEDULED</span>
                  <p className="mt-2 font-display font-black text-lg line-clamp-2">{s.title}</p>
                  <p className="text-sm text-zinc-600 mt-1">
                    {new Date(s.scheduled_at).toLocaleString()} · {s.seller_name}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {showListings && listings.length > 0 && (
          <section className="mt-8" data-testid="feed-listings">
            <h2 className="overline text-zinc-500 flex items-center gap-2">
              <PackageIcon size={14} className="text-[#1D78AB]" />
              LATEST DROPS
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-3">
              {listings.map((l) => (
                <ProductCard key={l.id} listing={l} />
              ))}
            </div>
          </section>
        )}

        {showPosts && posts.length > 0 && (
          <section className="mt-8" data-testid="feed-posts">
            <h2 className="overline text-zinc-500 flex items-center gap-2">
              <ChatCenteredDotsIcon size={14} className="text-violet-500" />
              POSTS
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              {posts.map((p) => (
                <PostCard key={p.id} post={p} currentUserId={user?.user_id} />
              ))}
            </div>
          </section>
        )}

        {tab === "people" && (
          <section className="mt-8" data-testid="feed-people">
            <h2 className="overline text-zinc-500 flex items-center gap-2">
              <UsersThreeIcon size={14} className="text-[#1D78AB]" />
              PEOPLE YOU FOLLOW
            </h2>
            {following === null ? (
              <div className="mt-4 text-sm text-zinc-500">Loading…</div>
            ) : following.length === 0 ? (
              <div className="mt-4 border border-zinc-200 p-10 text-center bg-zinc-50">
                <p className="font-display font-bold text-lg">You're not following anyone yet.</p>
                <p className="text-sm text-zinc-600 mt-1">
                  Find people from search up top, or browse live to follow your first seller.
                </p>
                <Link
                  to="/live"
                  className="mt-4 inline-block bg-[#1D78AB] text-white px-5 py-2.5 rounded-full text-sm font-semibold"
                >
                  Browse live
                </Link>
              </div>
            ) : (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {following.map((p) => {
                  const uid = p.user_id || p.seller_id;
                  const handle = p.username || p.seller_name || p.name || "user";
                  const liveStreamId = p.live_stream_id;
                  return (
                    <div
                      key={uid}
                      className="flex items-center gap-3 p-4 border border-zinc-200 rounded-xl bg-white relative"
                      data-testid={`following-row-${uid}`}
                    >
                      <Link
                        to={`/seller/${uid}`}
                        className="flex items-center gap-3 flex-1 min-w-0"
                      >
                        <div className="relative h-12 w-12 rounded-full bg-zinc-100 overflow-hidden flex-shrink-0">
                          {p.picture ? (
                            <img src={p.picture} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="h-full w-full grid place-items-center text-zinc-400 font-mono">@</div>
                          )}
                          {liveStreamId && (
                            <span
                              className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-rose-600 border-2 border-white animate-pulse"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-zinc-950 truncate">@{handle}</div>
                          <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 flex-wrap">
                            {p.is_seller === false ? "Buyer" : "Seller"}
                            {p.is_founder && (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-bold tracking-wider">
                                FOUNDER
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                      {liveStreamId ? (
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/stream/${liveStreamId}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-extrabold rounded-full bg-rose-600 text-white hover:bg-rose-700 shadow-sm tracking-wider"
                            data-testid={`live-now-btn-${uid}`}
                            title={`Tap to join @${handle}'s live show`}
                          >
                            <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                            LIVE
                          </Link>
                          <button
                            onClick={() => unfollow(uid)}
                            disabled={busyUserId === uid}
                            className="h-7 w-7 grid place-items-center rounded-full text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 disabled:opacity-50 transition-colors"
                            aria-label={`Unfollow @${handle}`}
                            title={`Unfollow @${handle}`}
                            data-testid={`unfollow-btn-${uid}`}
                          >
                            {busyUserId === uid ? "…" : "✕"}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => unfollow(uid)}
                          disabled={busyUserId === uid}
                          className="px-3 py-1.5 text-xs font-bold rounded-full border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50 transition-colors"
                          data-testid={`unfollow-btn-${uid}`}
                        >
                          {busyUserId === uid ? "…" : "Unfollow"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
